import { FC, useContext, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Button, Stack, Text } from '@chakra-ui/react';

import { convertToType } from '~/core/application/workflow/utils/transformFuncs';
import { UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import { useAddGuarantor, useGuarantorRoles } from '~/core/shared/api/plugin';
import {
  DatePicker,
  Dialog,
  FormField,
  Input,
  Phone,
  Select,
  Toggle,
} from '~/core/shared/components';
import { CategoryParent } from '~/core/shared/constants';
import { useFormResolver } from '~/core/shared/hooks';
import { useCategoriesOptions } from '~/core/shared/hooks/useCategoriesOptions';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { Option } from '~/core/shared/types';
import {
  ApplicationContext,
  DocumentDetailsViewActionContext,
} from '../Layout.component';

type GuarantorRole =
  | 'directors'
  | 'individual_guarantors'
  | 'company_guarantors';
type GuarantorType = 'individual' | 'company';
type AddressPrefix = 'registered_address' | 'trading_address';

type AddressFieldsProps = {
  prefix?: AddressPrefix;
  stateOptions: Option<string>[];
  countryOptions: Option<string>[];
};

type IndividualFieldsProps = {
  genderOptions: Option<string>[];
  stateOptions: Option<string>[];
  countryOptions: Option<string>[];
};

type CompanyFieldsProps = {
  stateOptions: Option<string>[];
  countryOptions: Option<string>[];
};

const dateValidationRegex = /^\d{4}-\d{2}-\d{2}$/;
const dateValidationMessage =
  'This field must match the following date format: DD/MM/YYYY';

const validationSchema = yup.object({
  abn: yup.string().matches(/^\d+$/, 'This field should contain only digits'),
  acn: yup.string().matches(/^\d+$/, 'This field should contain only digits'),
  role: yup.string().trim().required(),
  email: yup.string().email(),
  companyGuarantorDirectorEmail: yup.string().email(),
  dateOfBirth: yup.string().matches(dateValidationRegex, dateValidationMessage),
  dateOfAppointment: yup
    .string()
    .matches(dateValidationRegex, dateValidationMessage),
  abnStatusEffectiveFrom: yup
    .string()
    .matches(dateValidationRegex, dateValidationMessage),
});

const AddressFields: FC<AddressFieldsProps> = ({
  prefix,
  stateOptions,
  countryOptions,
}) => (
  <>
    <FormField
      component={Input}
      label="Address prefix"
      name={prefix ? `${prefix}~prefix` : 'prefix'}
      placeholder="Address room, floor, building, PO Box etc"
      size="sm"
    />

    <FormField
      component={Input}
      label="Street number"
      name={prefix ? `${prefix}~streetNumber` : 'streetNumber'}
      size="sm"
    />

    <FormField
      component={Input}
      label="Street type"
      name={prefix ? `${prefix}~streetType` : 'streetType'}
      size="sm"
    />

    <FormField
      component={Input}
      label="Street name"
      name={prefix ? `${prefix}~streetName` : 'streetName'}
      size="sm"
    />

    <FormField
      component={Input}
      label="Suburb"
      name={prefix ? `${prefix}~suburb` : 'suburb'}
      size="sm"
    />

    {/* <FormField component={Input}  label="City" name={prefix ? `${prefix}~city` : 'city'} size="sm" /> */}

    <FormField
      component={Input}
      label="Postcode"
      name={prefix ? `${prefix}~postcode` : 'postcode'}
      size="sm"
    />

    <FormField
      component={Select}
      label="State"
      name={prefix ? `${prefix}~state` : 'state'}
      options={stateOptions}
      size="md"
    />

    <FormField
      borderColor="gray.300"
      borderRadius="md"
      component={Select}
      label="Country"
      name={prefix ? `${prefix}~country` : 'country'}
      options={countryOptions}
      size="md"
      textTransform="capitalize"
      variant="outline"
    />
  </>
);

const IndividualFields: FC<IndividualFieldsProps> = ({
  genderOptions,
  stateOptions,
  countryOptions,
}) => (
  <>
    <FormField
      borderColor="gray.300"
      borderRadius="md"
      component={Input}
      label="First name"
      name="firstName"
      size="sm"
      variant="outline"
    />
    <FormField
      borderColor="gray.300"
      borderRadius="md"
      component={Input}
      label="Middle name"
      name="middleName"
      size="sm"
      variant="outline"
    />
    <FormField
      borderColor="gray.300"
      borderRadius="md"
      component={Input}
      label="Last name"
      name="lastName"
      size="sm"
      variant="outline"
    />
    <FormField
      borderColor="gray.300"
      borderRadius="md"
      component={Select}
      label="Gender"
      name="gender"
      options={genderOptions}
      size="md"
      textTransform="capitalize"
      variant="outline"
    />
    <FormField
      borderColor="gray.300"
      borderRadius="md"
      component={Input}
      label="Email"
      name="email"
      size="sm"
      variant="outline"
    />
    <FormField
      component={DatePicker}
      label="Date of birth"
      name="dateOfBirth"
    />
    <FormField
      component={DatePicker}
      label="Date of appointment"
      name="dateOfAppointment"
    />
    <FormField
      borderColor="gray.300"
      borderRadius="md"
      component={Phone}
      label="Phone number"
      name="phone"
      size="sm"
      variant="outline"
    />
    <AddressFields
      countryOptions={countryOptions}
      stateOptions={stateOptions}
    />
    <FormField
      component={Toggle}
      content="Is this person a homeowner?"
      name="isHomeowner"
    />
  </>
);

const CompanyFields: FC<CompanyFieldsProps> = ({
  stateOptions,
  countryOptions,
}) => (
  <>
    <FormField
      borderColor="gray.300"
      borderRadius="md"
      component={Input}
      label="ABN"
      maxLength={11}
      name="abn"
      size="sm"
      variant="outline"
    />

    <FormField
      borderColor="gray.300"
      borderRadius="md"
      component={Input}
      label="ACN"
      maxLength={9}
      name="acn"
      size="sm"
      variant="outline"
    />

    <FormField
      borderColor="gray.300"
      borderRadius="md"
      component={Input}
      label="Name"
      name="name"
      size="sm"
      variant="outline"
    />
    <FormField
      borderColor="gray.300"
      borderRadius="md"
      component={Input}
      label="Company guarantor director name"
      name="companyGuarantorDirectorName"
      size="sm"
      variant="outline"
    />
    <FormField
      borderColor="gray.300"
      borderRadius="md"
      component={Input}
      label="Company guarantor director email"
      name="companyGuarantorDirectorEmail"
      size="sm"
      variant="outline"
    />

    <FormField
      borderColor="gray.300"
      borderRadius="md"
      component={Input}
      label="Industry"
      name="industry"
      size="sm"
      variant="outline"
    />

    <FormField
      borderColor="gray.300"
      borderRadius="md"
      component={Input}
      label="Classification"
      name="classification"
      size="sm"
      variant="outline"
    />

    <FormField
      borderColor="gray.300"
      borderRadius="md"
      component={Input}
      label="ABN status"
      name="abnStatus"
      size="sm"
      variant="outline"
    />

    <FormField
      component={DatePicker}
      label="ABN status effective from"
      name="abnStatusEffectiveFrom"
    />

    <FormField
      borderColor="gray.300"
      borderRadius="md"
      component={Input}
      label="ABR type code"
      name="abrTypeCode"
      size="sm"
      variant="outline"
    />

    <FormField
      borderColor="gray.300"
      borderRadius="md"
      component={Input}
      label="ABR type name"
      name="abrTypeName"
      size="sm"
      variant="outline"
    />

    <FormField
      component={Toggle}
      content="GST registered?"
      name="gstRegistered"
    />

    <Text fontWeight="bold">Registered address</Text>
    <AddressFields
      countryOptions={countryOptions}
      prefix="registered_address"
      stateOptions={stateOptions}
    />

    <Text fontWeight="bold">Trading address</Text>
    <AddressFields
      countryOptions={countryOptions}
      prefix="trading_address"
      stateOptions={stateOptions}
    />
  </>
);

// @TODO: is this tenant-specific ? perhaps we need to refactor
export const AddGuarantorModal: FC = () => {
  const { actionContexts, applicationUuid } = useContext(ApplicationContext);

  const { roles } = useGuarantorRoles(applicationUuid);
  const { addGuarantor, isPending: isLoading } = useAddGuarantor();

  const [genderOptions] = useCategoriesOptions(CategoryParent.Gender);
  const [stateOptions] = useCategoriesOptions(CategoryParent.States);
  const [countryOptions] = useCategoriesOptions(CategoryParent.Countries);

  const {
    action: { key },
  } = useContext(DocumentDetailsViewActionContext);

  const addGuarantorModalApi: UseOpenCloseReturn = actionContexts[key].modal;

  // TODO: @haxxxton update to use form blocks and proper types
  const resolver = useFormResolver(validationSchema);
  const form = useForm({
    resolver,
  });

  const watchRole = form.watch('role') as GuarantorRole;

  const type = useMemo((): GuarantorType => {
    switch (watchRole) {
      case 'directors':
        return 'individual';
      case 'individual_guarantors':
        return 'individual';
      case 'company_guarantors':
        return 'company';
      default:
        return 'individual';
    }
  }, [watchRole]);

  const onSubmit = useEventHandler(
    async (formValues: Record<string, string>): Promise<void> => {
      const { isHomeowner, gstRegistered } = formValues;

      try {
        await addGuarantor({
          uuid: applicationUuid,
          data: {
            ...formValues,
            ...(isHomeowner
              ? { isHomeowner: convertToType('boolean')(isHomeowner) }
              : {}),
            ...(gstRegistered
              ? { gstRegistered: convertToType('boolean')(gstRegistered) }
              : {}),
          },
        });
        form.reset();
        addGuarantorModalApi.onClose();
      } catch {
        // error handled by global error handler
      }
    },
  );

  return (
    <Dialog
      header="Add Guarantor"
      onOpenChange={addGuarantorModalApi.onOpenChange}
      open={addGuarantorModalApi.open}
      scrollBehavior="inside"
      size="lg"
    >
      <FormProvider {...form}>
        <Stack as="form" gap={6} onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            borderColor="gray.300"
            borderRadius="md"
            component={Select}
            label="Role"
            name="role"
            options={roles}
            required
            size="md"
            textTransform="capitalize"
            variant="outline"
          />

          {type === 'individual' ? (
            <IndividualFields
              countryOptions={countryOptions}
              genderOptions={genderOptions}
              stateOptions={stateOptions}
            />
          ) : (
            <CompanyFields
              countryOptions={countryOptions}
              stateOptions={stateOptions}
            />
          )}

          <Stack direction="row" justifyContent="end">
            <Button
              colorPalette="gray"
              disabled={isLoading}
              fontSize="md"
              fontWeight="normal"
              onClick={addGuarantorModalApi.onClose}
              variant="outline"
            >
              Cancel
            </Button>

            <Button
              colorPalette="primary"
              fontSize="md"
              fontWeight="bold"
              loading={isLoading}
              type="submit"
              variant="solid"
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
    </Dialog>
  );
};
